import { newElementWith } from "../../packages/excalidraw/element/mutateElement";
import { getSizeFromPoints } from "../../packages/excalidraw/points";
import type {
  ExcalidrawElement,
  ExcalidrawLinearElement,
} from "../../packages/excalidraw/element/types";

import type { MindmapCustomData } from "../../packages/mindmap-core/src/mindmapElements";
import { mindmapConnectorGeometry } from "../../packages/mindmap-core/src/mindmapConnectorGeometry";
import {
  bindMindmapConnectors,
  indexMindmapNodes,
  nodeKey,
} from "../../packages/mindmap-core/src/mindmapBinding";

/**
 * Restore a map's canonical connector routes after its pills have moved (V3).
 *
 * Mindmap connectors ARE natively bound (see `mindmapBinding`), so a dragged
 * pill drags its arrows live — but Excalidraw's binding only moves the arrow's
 * two END POINTS, so mid-gesture the elbow bends out of shape. This pass runs
 * once the gesture ends and rewrites the whole point list from
 * `mindmapConnectorGeometry`, which is the sole authority on the route.
 *
 * Pure: given a scene, recompute every live connector from its parent's and
 * child's CURRENT boxes and return the next element array — or `null` when
 * nothing moved. That null is the loop guard: an unmoved connector recomputes
 * to exactly the points it already has, so committing the result can never
 * trigger another commit.
 *
 * An arrow's `x`/`y` is the ABSOLUTE position of its first point and `points`
 * are relative to it with `points[0] === [0,0]`, so a reroute must rewrite the
 * anchor and the point list together — and `width`/`height`, which Excalidraw's
 * own `mutateElement` derives from the points on every write but `newElementWith`
 * does not. Leaving them stale left every rerouted connector with bounds that no
 * longer matched its geometry.
 */

const mindmapOf = (el: ExcalidrawElement): MindmapCustomData | undefined =>
  (el.customData?.mindmap as MindmapCustomData | undefined) ?? undefined;

const samePoints = (
  element: ExcalidrawLinearElement,
  x: number,
  y: number,
  points: readonly (readonly [number, number])[],
): boolean => {
  const size = getSizeFromPoints(points as [number, number][]);
  if (
    element.x !== x ||
    element.y !== y ||
    element.width !== size.width ||
    element.height !== size.height
  ) {
    return false;
  }
  if (element.points.length !== points.length) {
    return false;
  }
  return element.points.every(
    (p, i) => p[0] === points[i][0] && p[1] === points[i][1],
  );
};

/**
 * A cheap fingerprint of every live mindmap element's box. The app shell keeps
 * the last one and only recomputes routes when it changes, so the pointerup
 * listener costs one string build per click instead of a full geometry pass.
 *
 * CONNECTORS are fingerprinted too, not just pills. With native binding the
 * drag bends the arrows as well as moving the pill, and a pill-only signature
 * missed every case where the arrows had drifted but the pills had not — a
 * resize, an undo, a collaborator's edit — leaving the map permanently on the
 * bent binding route instead of the canonical elbow.
 */
export const mindmapNodeSignature = (
  scene: readonly ExcalidrawElement[],
): string => {
  const parts: string[] = [];
  for (const el of scene) {
    const role = mindmapOf(el)?.role;
    if ((role === "node" || role === "connector") && !el.isDeleted) {
      parts.push(`${el.id}:${el.x}:${el.y}:${el.width}:${el.height}`);
    }
  }
  return parts.join("|");
};

export const rerouteMindmapConnectors = (
  scene: readonly ExcalidrawElement[],
): ExcalidrawElement[] | null => {
  // Bind first, so a map generated before connectors were bound (or restored
  // from a remembered subtree) gains its bindings on the very first gesture and
  // follows LIVE from then on. Idempotent: a bound scene returns null.
  const bound = bindMindmapConnectors(scene);
  const working = bound ?? scene;

  const nodes = indexMindmapNodes(working);
  if (nodes.size === 0) {
    return bound;
  }

  let changed = false;
  const next = working.map((el) => {
    const mm = mindmapOf(el);
    if (mm?.role !== "connector" || el.isDeleted) {
      return el;
    }
    // A connector is keyed by the CHILD block it points at; the child element
    // carries the parent link, so a reparent is followed for free.
    const child = nodes.get(nodeKey(mm.mapId, mm.blockId));
    const parentBlockId = child && mindmapOf(child)?.parentBlockId;
    const parent = parentBlockId
      ? nodes.get(nodeKey(mm.mapId, parentBlockId))
      : undefined;
    if (!child || !parent) {
      return el;
    }

    const { from, points } = mindmapConnectorGeometry(
      { x: parent.x, y: parent.y, width: parent.width, height: parent.height },
      { x: child.x, y: child.y, width: child.width, height: child.height },
      { departRatio: mm.departRatio },
    );
    const linear = el as ExcalidrawLinearElement;
    if (samePoints(linear, from.x, from.y, points)) {
      return el;
    }
    changed = true;
    return newElementWith(linear, {
      x: from.x,
      y: from.y,
      points,
      ...getSizeFromPoints(points),
    });
  });

  return changed ? next : bound;
};
